import React, { useEffect, useRef } from 'react';

/**
 * SUIVI DE STAGE — les FORMATIONS d'un processus de suivi (lot L3a, écart E171 ; E176)
 * ===========================================================================
 * Spec     : doc/modules/suivi_de_stage.md E171, E176
 * Maquette : doc/maquettes/suivi_de_stage_maquette.html (rév. 3 : `menuFormations`, `rendreFormations`, `#modaleNouveau` ;
 *            rév. 13 : sans semestre, « aussi dans « … » »)
 *
 * Un processus peut suivre PLUSIEURS formations (« BUT2 FI + BUT2 FIA »).
 * ⚠️ E176 (v90, 2026-10-22) — PLUS DE SEMESTRE, COMME LA CERTIFICATION : une formation se coche seule, ses étudiants sont
 * ceux de son INSCRIPTION de l'année ; créer un processus ne passe plus par le Programme de formation. Une formation
 * peut être suivie dans PLUSIEURS processus (décision Q1 de Bertrand) : le menu le DIT (« aussi dans « … » »), il ne
 * l'empêche plus — ses étudiants y ont alors un dossier dans chacun.
 *
 * Partagé par la fenêtre « Nouveau processus de suivi » (accueil) et par « Le processus » (éditeur d'une vignette) :
 * deux écrans qui calculeraient chacun « ce qui est cochable » finiraient par ne plus dire la même chose.
 *
 * L3b (E172) : les formations en apprentissage (FIA) sont proposées aussi — cocher une FIA, c'est suivre ses apprentis
 * ici (plus par le CFA), avec les mêmes étapes, sans convention ni documents de retour.
 */

/** E176 : les AUTRES processus de suivi de l'année (que `exclureId`) qui suivent déjà cette formation — leurs titres. */
export function autresProcessus(configurations, sousNiveauId, exclureId = null) {
    return (configurations || [])
        .filter((c) => c.id !== exclureId
            && (c.formations || [{ sous_niveau_id: c.sous_niveau_id }]).some((f) => f.sous_niveau_id === Number(sousNiveauId)))
        .map((c) => c.titre || c.formation_nom);
}

/**
 * Le nom PROPOSÉ — la même règle que le serveur (`titreProcessus`) : « BUT1 FI — Stage », « BUT2 — Stage ».
 * `libelle` : le semestre d'un processus d'AVANT la v90 (« BUT1 FI — Stage S2 ») ; absent ensuite (E176).
 */
export function nomPropose(noms, libelle) {
    if (!noms.length) return '';
    return `${prefixeFormations(noms)} — Stage${libelle ? ` ${libelle}` : ''}`;
}

/**
 * Le PRÉFIXE des formations : « BUT1 FI » (une), « BUT2 » (même premier mot), sinon « BUT2 FI + LP QLIO ».
 * ⚠️ Séparé de `nomPropose` (E176) : la certification nomme « BUT2 — Rapport », jamais « … — Stage ».
 */
export function prefixeFormations(noms) {
    if (!noms.length) return '';
    if (noms.length === 1) return noms[0];
    const premiers = [...new Set(noms.map((n) => String(n).split(' ')[0]))];
    return premiers.length === 1 ? premiers[0] : noms.join(' + ');
}

/** Les formations proposées (celles qui ont des inscrits, quel que soit leur régime), dans l'ordre de la liste du serveur. */
export const formationsProposees = (donnees) => (donnees.formations || [])
    .filter((f) => (f.nb_etudiants_fi || 0) + (f.nb_apprentis || 0) + (f.nb_fc || 0) + (f.nb_sans_regime || 0) > 0);

/**
 * E178 (v91, maquette rév. 15) — les RÉGIMES d'inscription : une formation peut mêler les trois. « Qui est suivi ici »
 * a une case par régime présent ; une formation nouvellement cochée suit la formation initiale seule (Q1) ; apprentis et
 * FC non suivis ici → CFA (Q3).
 */
export const REGIMES = [
    { cle: 'initiale', libelle: 'Formation initiale', nb: (f) => f?.nb_etudiants_fi || 0 },
    { cle: 'apprentis', libelle: 'Apprentis', nb: (f) => f?.nb_apprentis || 0 },
    { cle: 'fc', libelle: 'Formation continue', nb: (f) => f?.nb_fc || 0 }
];

/** « 13 stagiaires », « 28 apprentis », « 56 stagiaires · 1 apprenti · 2 FC » (L3b, E178). */
export function populationDe(f) {
    const s = f?.nb_etudiants_fi || 0, a = f?.nb_apprentis || 0, c = f?.nb_fc || 0;
    return [s ? `${s} stagiaire${s > 1 ? 's' : ''}` : null, a ? `${a} apprenti${a > 1 ? 's' : ''}` : null, c ? `${c} FC` : null]
        .filter(Boolean).join(' · ') || '0 étudiant';
}
/** Une formation d'apprentissage (que des apprentis) : sa puce est violette, comme dans la maquette. */
export const estApprentissage = (f) => !!f && (f.nb_apprentis || 0) > 0 && !(f.nb_etudiants_fi || 0);

/** Les identifiants cochés, remis dans l'ordre de la liste (la première devient la formation « principale »). */
export const dansLOrdre = (donnees, ids) => (donnees.formations || []).map((f) => f.id).filter((id) => ids.includes(id));

/**
 * Le menu déroulant à cases (maquette rév. 3, rév. 13). E176 : toutes les formations ayant des inscrits sont cochables ;
 * une formation déjà suivie dans un autre processus le dit (« aussi dans « … » ») sans être bloquée (Q1).
 */
export function MenuFormations({ donnees, coches, exclureId = null, onBasculer, disabled = false }) {
    const ref = useRef(null);
    // Un clic ailleurs referme le menu (le <details> natif ne le fait pas).
    useEffect(() => {
        const ailleurs = (e) => { if (ref.current && ref.current.open && !ref.current.contains(e.target)) ref.current.open = false; };
        document.addEventListener('mousedown', ailleurs);
        return () => document.removeEventListener('mousedown', ailleurs);
    }, []);
    const proposees = formationsProposees(donnees);
    const groupes = [];
    for (const f of proposees) {
        const g = String(f.nom).split(' ')[0];
        const dernier = groupes[groupes.length - 1];
        if (dernier && dernier.nom === g) dernier.formations.push(f); else groupes.push({ nom: g, formations: [f] });
    }
    const nomDe = (id) => (donnees.formations || []).find((f) => f.id === id)?.nom || `#${id}`;
    return (
        <details className={'pf-multi' + (disabled ? ' off' : '')} ref={ref} onToggle={(e) => { if (disabled) e.currentTarget.open = false; }}>
            <summary>
                {coches.length
                    ? coches.map((id) => <span key={id} className={'pf-chip' + (estApprentissage((donnees.formations || []).find((f) => f.id === id)) ? ' violet' : '')}>{nomDe(id)}</span>)
                    : <span className="pf-vide">Choisir les formations…</span>}
            </summary>
            <div className="pf-liste">
                {!proposees.length && <div className="pf-grp">Aucune formation avec des étudiants inscrits</div>}
                {groupes.map((g) => (
                    <React.Fragment key={g.nom}>
                        <div className="pf-grp">{g.nom}</div>
                        {g.formations.map((f) => {
                            const ailleurs = autresProcessus(donnees.configurations, f.id, exclureId);
                            return (
                                <label key={f.id}>
                                    <input type="checkbox" checked={coches.includes(f.id)} disabled={disabled} onChange={() => onBasculer(f.id)} /> {f.nom}
                                    <small>
                                        {populationDe(f)}
                                        {ailleurs.length > 0 && <span className="pf-ailleurs"> · aussi dans « {ailleurs.join(' », « ')} »</span>}
                                    </small>
                                </label>
                            );
                        })}
                    </React.Fragment>
                ))}
            </div>
        </details>
    );
}

/**
 * E171 (fin, v86) — le menu à cases de la CERTIFICATION : toutes les formations actives du département, apprentis
 * compris ; ni semestre ni « déjà dans » — une formation peut être dans PLUSIEURS processus de certification.
 * `formations` = [{ id, nom, nb_etudiants, nb_alternants }] (paramétrage de la certification).
 */
export function MenuFormationsCertif({ formations, coches, onBasculer, disabled = false }) {
    const ref = useRef(null);
    useEffect(() => {
        const ailleurs = (e) => { if (ref.current && ref.current.open && !ref.current.contains(e.target)) ref.current.open = false; };
        document.addEventListener('mousedown', ailleurs);
        return () => document.removeEventListener('mousedown', ailleurs);
    }, []);
    const groupes = [];
    for (const f of formations || []) {
        const g = String(f.nom).split(' ')[0];
        const dernier = groupes[groupes.length - 1];
        if (dernier && dernier.nom === g) dernier.formations.push(f); else groupes.push({ nom: g, formations: [f] });
    }
    const nomDe = (id) => (formations || []).find((f) => f.id === id)?.nom || `#${id}`;
    return (
        <details className={'pf-multi' + (disabled ? ' off' : '')} ref={ref} onToggle={(e) => { if (disabled) e.currentTarget.open = false; }}>
            <summary>
                {coches.length
                    ? coches.map((id) => <span key={id} className="pf-chip">{nomDe(id)}</span>)
                    : <span className="pf-vide">Choisir les formations…</span>}
            </summary>
            <div className="pf-liste">
                {!groupes.length && <div className="pf-grp">Aucune formation active dans le département</div>}
                {groupes.map((g) => (
                    <React.Fragment key={g.nom}>
                        <div className="pf-grp">{g.nom}</div>
                        {g.formations.map((f) => (
                            <label key={f.id}>
                                <input type="checkbox" checked={coches.includes(f.id)} disabled={disabled} onChange={() => onBasculer(f.id)} /> {f.nom}
                                <small>{etudiantsDe(f)}</small>
                            </label>
                        ))}
                    </React.Fragment>
                ))}
            </div>
        </details>
    );
}

/** « 29 étudiants (dont 28 apprentis) » — ce que compte une formation de la certification. */
export function etudiantsDe(f) {
    const n = f?.nb_etudiants || 0;
    const a = f?.nb_alternants || 0;
    return `${n} étudiant${n > 1 ? 's' : ''}${a && a < n ? ` (dont ${a} apprenti${a > 1 ? 's' : ''})` : a && a === n ? ' (apprentis)' : ''}`;
}

/** Qui certifie, pour une formation (maquette rév. 3) : le maître de stage (FI) ou d'apprentissage (apprentis). */
export function quiCertifie(f) {
    const n = f?.nb_etudiants || 0;
    const a = f?.nb_alternants || 0;
    if (a && a === n) return "Le maître d'apprentissage (registre Alternance)";
    if (!a) return 'Le maître de stage (fiche de liaison validée)';
    return "Selon l'étudiant : le maître de stage (fiche de liaison) ou d'apprentissage (registre Alternance)";
}
